// src/domains/sales/deals/forms/create-bko-form/sales-deals-bko-create-form.jsx

import { InputField, InputSelectOptions, ThousandsInputField, ToggleField } from 'components/molecules'
import { FieldArray, Form, Formik, useFormikContext } from 'formik'
import PropTypes from 'prop-types'
import { Alert, FormText, Button, Input, Label, Row, Col, Card, CardBody } from 'reactstrap'
import SalesDealsSchema from '../create-form/sales-deals-schema' // reuse SAME schema
import UserSelectSearchField from '../../organisms/user-select-search-field'
import { CREATED_DEAL_BY, CREATED_DEAL_BY_OPTIONS, SALES_METHOD_CREATE_OPTIONS, PAYMENT_METHOD, EDC_PAYMENT_METHODS, CARD_TYPE_OPTIONS, SALES_METHOD_NOTE_REQUIRED_METHODS, SALES_METHOD_NOTE_LABEL_BY_METHOD } from '../../constants'
import { useEffect, useState } from 'react'
import SalesDealsApi from 'api/admins/sales-deals-api'
import SalesUsersApi from 'api/admins/sales-users-api'
import CourierSelectSearchField from '../../organisms/courier-select-search-field'
import PaymentMethodSelectSearchField from '../../organisms/payment-method-select-search-field'
import PhoneCodeSelectSearchField from '../../organisms/phone-code-select-search-field'
// import UserAddressPinpoint from '../../organisms/address-pinpoint'
import DateInputField from 'components/molecules/date-input-field'
import LeadSourceSelectSearchField from '../../organisms/lead-source-select-search-field'
import SalesAliasSelectSearchField from '../../organisms/sales-alias-select-search-field'
import SalesNameSelectSearchField from '../../organisms/sales-name-select-search-field'

const propTypes = {
  onSubmitForm            : PropTypes.func.isRequired,
  checkUserEmailIsUnique  : PropTypes.func,
  checkUserPhoneIsUnique  : PropTypes.func,
  onValidationError       : PropTypes.func,
}

const MOBILE_SCROLL_OFFSET = 140

const REQUIRED_FIELD_LABELS = {
  user: 'Cari Customer',
  email: 'Email Customer',
  sales_name_in_invoice: 'Nama Sales di Invoice',
  deal_by: 'Deal By',
  'assist_by_list.0': 'Assist By 1',
  created_deal_by: 'Create Deal By',
  sales_method: 'Metode Penjualan',
  items: 'PO Item',
  po_designer_id: 'Brand / Designer',
  po_category_id: 'Kategori',
  po_condition_id: 'Condition',
  po_comes_with: 'Kelengkapan / Comes With',
  courier: 'Kurir',
  down_payment: 'Down Payment',
  dp_transfer_proofs: 'Bukti Transfer DP',
  'payment_method_list.0': 'Metode Pembayaran 1',
  payment_method: 'Metode Pembayaran 1',
  card_type: 'Card Type',
}

const FIELD_ORDER = [
  'user',
  'email',
  'sales_name_in_invoice',
  'deal_by',
  'assist_by_list.0',
  'sales_method',
  'created_deal_by',
  'items',
  'items.*.selling_price',
  'po_designer_id',
  'po_category_id',
  'po_condition_id',
  'po_comes_with',
  'courier',
  'down_payment',
  'dp_transfer_proofs',
  'payment_method_list.0',
  'payment_method',
  'card_type',
]

const FIELD_TARGET_ALIASES = {
  user_id: ['user'],
  items: ['items.0.selling_price'],
  payment_method: ['payment_method_list.0', 'payment_method'],
}

const hasMeaningfulValue = (value) => {
  if (value === null || value === undefined) return false

  if (typeof value === 'string') {
    return value.trim().length > 0
  }

  if (typeof value === 'number') {
    return Number.isFinite(value)
  }

  if (typeof value === 'boolean') {
    return value === true
  }

  if (Array.isArray(value)) {
    return value.some(hasMeaningfulValue)
  }

  if (typeof value === 'object') {
    return Object.values(value).some(hasMeaningfulValue)
  }

  return Boolean(value)
}

const isBlank = (value) => !hasMeaningfulValue(value)

const getPathValue = (obj, path) => {
  if (!obj || !path) return undefined
  return path.split('.').reduce((acc, key) => {
    if (acc === null || acc === undefined) return undefined
    return acc[key]
  }, obj)
}

const flattenErrorPaths = (errorValue, prefix = '') => {
  if (!errorValue) return []
  if (typeof errorValue === 'string') return prefix ? [prefix] : []

  if (Array.isArray(errorValue)) {
    return errorValue.flatMap((item, index) =>
      flattenErrorPaths(item, prefix ? `${prefix}.${index}` : `${index}`)
    )
  }

  if (typeof errorValue === 'object') {
    return Object.keys(errorValue).flatMap((key) =>
      flattenErrorPaths(errorValue[key], prefix ? `${prefix}.${key}` : key)
    )
  }

  return prefix ? [prefix] : []
}

const buildTouchedFromErrors = (errorValue) => {
  if (!errorValue) return undefined
  if (typeof errorValue === 'string') return true
  if (Array.isArray(errorValue)) return errorValue.map(buildTouchedFromErrors)

  if (typeof errorValue === 'object') {
    return Object.keys(errorValue).reduce((acc, key) => {
      acc[key] = buildTouchedFromErrors(errorValue[key])
      return acc
    }, {})
  }

  return true
}

const mergeDeep = (base, next) => {
  if (Array.isArray(next)) return next
  if (!next || typeof next !== 'object') return next

  return Object.keys(next).reduce((acc, key) => {
    acc[key] = mergeDeep(base?.[key], next[key])
    return acc
  }, { ...(base || {}) })
}

const matchOrderedField = (orderedField, errorPaths) => {
  if (orderedField.includes('*')) {
    const regex = new RegExp(`^${orderedField.replace(/\./g, '\\.').replace('\\*', '\\d')}$`)
    return errorPaths.find((path) => regex.test(path))
  }

  return errorPaths.find((path) => path === orderedField || path.startsWith(`${orderedField}.`))
}

const getFirstErrorField = (errors = {}) => {
  const paths = flattenErrorPaths(errors)
  if (!paths.length) return null

  for (const field of FIELD_ORDER) {
    const matched = matchOrderedField(field, paths)
    if (matched) return matched
  }

  return paths[0]
}

const getFieldLabel = (path) => {
  if (!path) return 'Field wajib'
  if (REQUIRED_FIELD_LABELS[path]) return REQUIRED_FIELD_LABELS[path]
  if (/^items\.\d+\.selling_price$/.test(path)) return 'Final Price item'
  if (/^assist_by_list\.\d+$/.test(path)) {
    const idx = Number(path.split('.')[1])
    return `Assist By ${idx + 1}`
  }
  if (/^payment_method_list\.\d+$/.test(path)) {
    const idx = Number(path.split('.')[1])
    return `Metode Pembayaran ${idx + 1}`
  }
  return path
    .split('.')
    .pop()
    .replace(/_/g, ' ')
}

const buildSubmitErrorSummary = (errors = {}) => {
  const paths = flattenErrorPaths(errors)
  const seen = new Set()

  return paths
    .map((path) => {
      const message = getPathValue(errors, path)
      return typeof message === 'string'
        ? message
        : `${getFieldLabel(path)} wajib diisi.`
    })
    .filter((message) => {
      if (!message || seen.has(message)) return false
      seen.add(message)
      return true
    })
}

const clearSubmitErrorTarget = () => {
  document
    .querySelectorAll('.bnk-submit-error-target')
    .forEach((el) => el.classList.remove('bnk-submit-error-target'))
}

const queryFieldTarget = (fieldName) => {
  const names = [
    fieldName,
    ...(FIELD_TARGET_ALIASES[fieldName] || []),
  ].filter(Boolean)

  for (const name of names) {
    const safeName = String(name).replace(/"/g, '\\"')
    const target =
      document.querySelector(`[data-required-field="${safeName}"]`) ||
      document.querySelector(`[name="${safeName}"]`)

    if (target) return target
  }

  return null
}

const scrollToFirstInvalidField = (fieldName) => {
  const targetEl = queryFieldTarget(fieldName)
  if (!targetEl) return

  clearSubmitErrorTarget()

  const scrollTarget =
    targetEl.closest('[data-required-field], .form-group, .mb-3, .border') ||
    targetEl

  scrollTarget.classList.add('bnk-submit-error-target')

  const top =
    scrollTarget.getBoundingClientRect().top +
    window.pageYOffset -
    MOBILE_SCROLL_OFFSET

  window.scrollTo({
    top: Math.max(top, 0),
    behavior: 'smooth',
  })

  window.setTimeout(() => {
    const focusTarget =
      targetEl.matches?.('input, select, textarea, button')
        ? targetEl
        : targetEl.querySelector?.('input, select, textarea, button, [tabindex]')

    if (focusTarget?.focus) {
      focusTarget.focus({ preventScroll: true })
    }
  }, 350)
}

const SubmitErrorSummarySyncer = ({ enabled, setSubmitErrorSummary }) => {
  const { errors } = useFormikContext()

  useEffect(() => {
    if (!enabled) return
    setSubmitErrorSummary(buildSubmitErrorSummary(errors))
  }, [enabled, errors, setSubmitErrorSummary])

  return null
}


const splitFullName = (fullName = '') => {
  const cleaned = (fullName || '').trim().replace(/\s+/g, ' ')
  if(!cleaned) return { first: '', last: '' }
  const parts = cleaned.split(' ')
  const first = parts.shift() || ''
  const last  = parts.join(' ')
  return { first, last }
}

const buildFullName = (firstName, lastName) => {
  const first = (firstName || '').trim()
  const last  = (lastName || '').trim()
  return [ first, last ].filter(Boolean).join(' ').trim()
}

const toIntOrNull = (v) => {
  if (v === null || v === undefined || v === '') return null
  const n = parseInt(v, 10)
  return Number.isFinite(n) ? n : null
}

const toNumberOrNull = (v) => {
  if (v === null || v === undefined || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

const isItemComplete = (item) => {
  const n = toNumberOrNull(item?.selling_price)
  return n !== null && n > 0
}

/**
 * ✅ Auto-sync assist_by (string) from assist_by_list (array)
 * Keeps old API contract safe.
 */
const AssistBySyncer = () => {
  const { values, setFieldValue } = useFormikContext()

  useEffect(() => {
    const list = (values.assist_by_list || [])
      .map((x) => (x || '').toString().trim())
      .filter(Boolean)

    const joined = list.join(', ')
    const current = (values.assist_by || '').toString()

    if (current !== joined) {
      setFieldValue('assist_by', joined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.assist_by_list])

  return null
}

const PaymentMethodSyncer = () => {
  const { values, setFieldValue } = useFormikContext()

  useEffect(() => {
    const list = Array.isArray(values.payment_method_list) ? values.payment_method_list : []

    const first = list.find((x) => {
      if (!x) return false
      if (typeof x === 'string') return x.trim().length > 0
      return true
    }) || null

    if (values.payment_method !== first) {
      setFieldValue('payment_method', first)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.payment_method_list])

  return null
}

const getPaymentMethodValue = (paymentMethod) => {
  if (!paymentMethod) return null
  if (typeof paymentMethod === 'string') return paymentMethod.trim() || null
  return paymentMethod?.value || null
}

const isMidtransCreditCardPaymentMethod = (paymentMethod) => {
  return getPaymentMethodValue(paymentMethod) === PAYMENT_METHOD.CREDIT_CARD
}

const getDpValidationFinalPayment = (values = {}) => {
  if ((values.voucher_code || '').toString().trim()) return null

  const items = Array.isArray(values.items) ? values.items : []
  if (!items.length || !items.every(isItemComplete)) return null

  const subtotal = items.reduce((sum, item) => {
    return sum + (toNumberOrNull(item?.selling_price) || 0)
  }, 0)

  if (!(subtotal > 0)) return null

  const paymentMethod =
    values.payment_method ||
    (Array.isArray(values.payment_method_list) ? values.payment_method_list[0] : null)

  if (!isMidtransCreditCardPaymentMethod(paymentMethod)) return subtotal

  const cardType =
    paymentMethod && typeof paymentMethod === 'object'
      ? paymentMethod.card_type
      : values.card_type

  const netRate = cardType === 'amex' ? 0.97 : 0.974
  return Math.round((subtotal / netRate) * 100) / 100
}

const normalizeMidtransVirtualAccountPaymentMethod = (paymentMethod) => {
  if (!paymentMethod) return paymentMethod

  if (typeof paymentMethod === 'string') {
    return paymentMethod === PAYMENT_METHOD.MIDTRANS
      ? PAYMENT_METHOD.VIRTUAL_ACCOUNT
      : paymentMethod
  }

  if (paymentMethod?.value !== PAYMENT_METHOD.MIDTRANS) return paymentMethod

  return {
    ...paymentMethod,
    label: 'MIDTRANS (VA)',
    value: PAYMENT_METHOD.VIRTUAL_ACCOUNT,
  }
}

const normalizeMidtransVirtualAccountPaymentMethodList = (list = []) => {
  return (Array.isArray(list) ? list : []).map(normalizeMidtransVirtualAccountPaymentMethod)
}

const PaymentMethodCardTypeSyncer = ({ onPrimaryPaymentMethodChange }) => {
  const { values, setFieldValue } = useFormikContext()

  useEffect(() => {
    const methodValue = getPaymentMethodValue(values.payment_method)

    onPrimaryPaymentMethodChange(methodValue)

    if (!EDC_PAYMENT_METHODS.includes(methodValue) && values.card_type) {
      setFieldValue('card_type', null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.payment_method, values.card_type])

  return null
}

/**
 * ✅ Ensures a PO Placeholder product is auto-added as the only item for BKO/PO deal creation.
 * (No SKU/product name/supplier input at create time.)
 */
const PoProductBootstrapper = ({ poProduct }) => {
  const { values, setFieldValue } = useFormikContext()

  useEffect(() => {
    if (!poProduct?.id) return

    const items = values.items || []
    const currentItem = items[0] || {}

    if (items.length === 1 && String(currentItem.product_id) === String(poProduct.id)) return

    setFieldValue('product_ids', [poProduct])
    setFieldValue('items', [
      {
        product_id: poProduct.id,
        selling_price: currentItem.selling_price || '',
        authenticator: currentItem.authenticator || '',
        authenticity_check_note: currentItem.authenticity_check_note || '',
        include_authenticity_check: currentItem.include_authenticity_check || false,
        note: currentItem.note || '', // use this as "PO product detail" free text
      }
    ])

    // reset courier/payment because SKU/placeholder changed after Brand/Category selection
    setFieldValue('courier', null)
    setFieldValue('shipment_cost', 0)
    setFieldValue('payment_method_list', [''])
    setFieldValue('payment_method', null)
    setFieldValue('card_type', null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poProduct?.id])

  return null
}

const PoProductMetaLoader = ({ setPoProduct, setPoProductLoading, setPoProductError }) => {
  const { values, setFieldValue } = useFormikContext()

  useEffect(() => {
    const designerId = toIntOrNull(values.po_designer_id)
    const categoryId = toIntOrNull(values.po_category_id)

    if (!designerId || !categoryId) {
      setPoProduct(null)
      setPoProductError(null)
      setPoProductLoading(false)

      if ((values.items || []).length > 0) {
        setFieldValue('product_ids', [])
        setFieldValue('items', [])
        setFieldValue('courier', null)
        setFieldValue('shipment_cost', 0)
        setFieldValue('payment_method_list', [''])
        setFieldValue('payment_method', null)
        setFieldValue('card_type', null)
      }

      return undefined
    }

    let mounted = true

    ;(async () => {
      setPoProductLoading(true)
      setPoProductError(null)

      try {
        const res = await SalesDealsApi.poProductMeta({
          designer_id: designerId,
          category_id: categoryId,
        })

        if (!mounted) return

        const prod = res?.data?.data?.product
        if (!prod?.id) {
          setPoProduct(null)
          setPoProductError('PO Placeholder product tidak ditemukan untuk Brand/Category ini.')
        } else {
          setPoProduct(prod)
        }
      } catch (e) {
        if (!mounted) return
        setPoProduct(null)
        setPoProductError(e?.response?.data?.message || 'Gagal load PO Placeholder product sesuai Brand/Category.')
      } finally {
        if (mounted) setPoProductLoading(false)
      }
    })()

    return () => { mounted = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.po_designer_id, values.po_category_id])

  return null
}

const BlackSectionHeader = ({ title, subtitle }) => (
  <div
    style={{
      background: "#000",
      color: "#fff",
      padding: "12px 16px",
      borderRadius: 6,
      marginBottom: 16,
    }}
  >
    <div style={{ fontWeight: 800, letterSpacing: 0.3 }}>{title}</div>
    {subtitle && (
      <div style={{ fontSize: 12, opacity: 0.9, marginTop: 4 }}>
        {subtitle}
      </div>
    )}
  </div>
)

const SectionTitle = ({ title, subtitle }) => (
  <div style={{ marginBottom: 10 }}>
    <h4 style={{ marginBottom: 6 }}>{title}</h4>
    {subtitle && <FormText style={{ marginTop: 0 }}>{subtitle}</FormText>}
  </div>
)

const getUserAddressPageLink = (userId) => (
  userId ? `/app/sales/users/${userId}` : '/app/sales/users'
)

const PinpointMovedNote = ({ userId }) => (
  <Alert color='warning' style={{ marginTop: 12, marginBottom: 16 }}>
    <div>
      Set Google Map / Pinpoint sudah tidak di halaman Deals, silakan set di <b>User Address</b>.
    </div>

    <div style={{ marginTop: 6 }}>
      <a
        href={getUserAddressPageLink(userId)}
        target='_blank'
        rel='noopener noreferrer'
      >
        Klik edit di sini
      </a>
    </div>

    {!userId && (
      <FormText className='text-muted' style={{ marginTop: 8 }}>
        Pilih customer terlebih dahulu agar link mengarah langsung ke user yang dipilih.
      </FormText>
    )}
  </Alert>
)

const SalesDealsBkoCreateForm = ({
  onSubmitForm,
  checkUserEmailIsUnique = () => {},
  checkUserPhoneIsUnique = () => {},
  onValidationError = () => {},
}) => {
  const initialValues = {
    // CUSTOMER
    use_existing_user: true,
    edit_existing_user: false,
    hide_existing_user_detail: true,

    user_id: null,
    user_detail_id: null,
    address_id: null,
    email: "",
    password: "",
    birthdate: null,

    // split name UI
    first_name: '',
    last_name: '',
    // legacy full name
    name: '',

    phone: "",
    phone_code: '+62',
    instagram_account: "",
    lead_source: null,

    complete_address: "",
    recipient_name: "",
    recipient_number: "",
    instruction: "",
    latitude: "",
    longitude: "",

    // SALES
    sales_name_in_invoice: null,
    deal_by: null,

    // ✅ keep old field (string) for backward compatibility
    assist_by: "",
    // ✅ new field (array)
    assist_by_list: [""],

    po_designer_id: '',
    po_category_id: '',
    po_condition_id: '',
    po_comes_with: '',

    // DEAL
    created_deal_by: CREATED_DEAL_BY.ADMIN,
    sales_method: null,
    sales_method_note: '',

    // ITEMS
    product_search: [],
    product_ids: [],
    items: [],

    // SHIPMENT
    pickup_at: null,
    courier: null,
    shipment_cost: 0,

    // ORDER
    voucher_code: "",
    include_invoice: true,
    include_polaroid: false,
    include_love_letter: false,
    note: '',

    down_payment: null,
    dp_transfer_proofs: [],

    // PAYMENT (last)
    payment_method: null,
    payment_method_list: [''],
    card_type: null,
  }

  // Google Maps pinpoint is intentionally disabled on this form.
  // Keep the UI block commented below, but do not load Google Maps script here
  // to prevent API-key/billing popups on pages that do not render the map.

  // const [ showPinpoint, setShowPinpoint ] = useState(false)
  const [ submitErrorSummary, setSubmitErrorSummary ] = useState([])
  const [ primaryPaymentMethodValue, setPrimaryPaymentMethodValue ] = useState(null)

  // ===== User meta (details/addresses) for existing user =====
  const [ userMeta, setUserMeta ] = useState(null)
  const [ isUserMetaLoading, setIsUserMetaLoading ] = useState(false)
  const [ userMetaError, setUserMetaError ] = useState('')

  // ✅ PO Placeholder product meta
  const [ poProduct, setPoProduct ] = useState(null)
  const [ poProductLoading, setPoProductLoading ] = useState(false)
  const [ poProductError, setPoProductError ] = useState(null)

  const [poMeta, setPoMeta] = useState({ designers: [], categories: [], conditions: [] })
  const [poMetaLoading, setPoMetaLoading] = useState(false)

  useEffect(() => {
    let mounted = true

    ;(async () => {
      setPoMetaLoading(true)

      try {
        const res = await SalesDealsApi.salesProductMeta()

        if (!mounted) return

        setPoMeta({
          designers: res?.data?.data?.designers || [],
          categories: res?.data?.data?.categories || [],
          conditions: res?.data?.data?.conditions || [],
        })
      } catch (e) {
        if (!mounted) return
        setPoMeta({ designers: [], categories: [], conditions: [] })
      } finally {
        if (mounted) setPoMetaLoading(false)
      }
    })()

    return () => { mounted = false }
  }, [])

  const syncFullNameIntoLegacyName = (formikBag, firstName, lastName) => {
    formikBag.setFieldValue('name', buildFullName(firstName, lastName))
  }

  // ===== User detail/address helpers (for existing user selection) =====
  const pickPrimary = (arr) => {
    const list = Array.isArray(arr) ? arr.filter(Boolean) : []
    if (!list.length) return null
    return list.find(x => x?.primary) || list[0]
  }

  const getDetailLabel = (d) => {
    if (!d) return "-"
    const name = (d.name || [d.first_name, d.last_name].filter(Boolean).join(" ")).trim()
    const phone = (d.phone || "").toString().trim()
    return [name, phone].filter(Boolean).join(" - ") || `Detail #${d.id}`
  }

  const getAddressLabel = (a) => {
    if (!a) return "-"
    const addr = (a.complete_address || "").toString().trim()
    if (addr) return addr.length > 80 ? `${addr.slice(0, 80)}…` : addr
    const rec = [a.recipient_name, a.recipient_number].filter(Boolean).join(" / ")
    return rec ? `(${rec}) Address #${a.id}` : `Address #${a.id}`
  }

  const applyUserDetailToFormik = (detail, formikBag) => {
    if (!detail || !formikBag) return
    formikBag.setFieldValue('user_detail_id', detail.id)

    const firstName = (detail.first_name || '').toString()
    const lastName  = (detail.last_name || '').toString()
    const fullName  = (detail.name || '').toString()

    if (firstName || lastName) {
      formikBag.setFieldValue('first_name', firstName)
      formikBag.setFieldValue('last_name', lastName)
      syncFullNameIntoLegacyName(formikBag, firstName, lastName)
    } else if (fullName) {
      const split = splitFullName(fullName)
      formikBag.setFieldValue('first_name', split.first_name)
      formikBag.setFieldValue('last_name', split.last_name)
      syncFullNameIntoLegacyName(formikBag, split.first_name, split.last_name)
    }

    if (detail.phone !== undefined) {
      formikBag.setFieldValue('phone', detail.phone || '')
    }
  }

  const applyUserAddressToFormik = (addr, formikBag) => {
    if (!addr || !formikBag) return
    formikBag.setFieldValue('address_id', addr.id)

    formikBag.setFieldValue('complete_address', addr.complete_address || '')
    formikBag.setFieldValue('recipient_name', addr.recipient_name || '')
    formikBag.setFieldValue('recipient_number', addr.recipient_number || '')
    formikBag.setFieldValue('instruction', addr.instruction || '')

    if (addr.latitude !== undefined) formikBag.setFieldValue('latitude', addr.latitude)
    if (addr.longitude !== undefined) formikBag.setFieldValue('longitude', addr.longitude)
  }

  const fetchAndApplyUserMeta = async (userId, formikBag, preferredDetailId = null) => {
    if (!userId) {
      setUserMeta(null)
      setUserMetaError('')
      return
    }

    setIsUserMetaLoading(true)
    setUserMetaError('')

    try {
      const response = await SalesUsersApi.show({ userId })
      const fullUser = response?.data?.user || response?.data?.data || response?.data

      setUserMeta(fullUser || null)

      const details = Array.isArray(fullUser?.details) ? fullUser.details : []
      const addresses = Array.isArray(fullUser?.addresses) ? fullUser.addresses : []

      const preferred =
        preferredDetailId
          ? details.find(d => String(d?.id) === String(preferredDetailId))
          : null

      const defaultDetail = preferred || pickPrimary(details)
      const defaultAddress = pickPrimary(addresses)

      if (defaultDetail) applyUserDetailToFormik(defaultDetail, formikBag)
      if (defaultAddress) applyUserAddressToFormik(defaultAddress, formikBag)
    } catch (err) {
      console.error(err)
      setUserMeta(null)
      setUserMetaError(err?.response?.data?.message || err?.message || 'Failed to load user meta')
    } finally {
      setIsUserMetaLoading(false)
    }
  }

  return (
    <Formik
      initialValues={ initialValues }
      validationSchema={ SalesDealsSchema }
      validate={(values) => {
        const errors = {}

        const dp = toNumberOrNull(values.down_payment)
        const files = values.dp_transfer_proofs || []
        const methodValue = getPaymentMethodValue(values.payment_method)
        const isMidtransCC = isMidtransCreditCardPaymentMethod(values.payment_method)
        const items = values.items || []
        const firstAssistBy = (values.assist_by_list || [])[0]
        const firstPaymentMethod = (values.payment_method_list || [])[0]

        if (values.use_existing_user && !values.user_id) {
          errors.user = 'Pilih customer terlebih dahulu.'
        }

        if (!values.use_existing_user && isBlank(values.email)) {
          errors.email = 'Email Customer wajib diisi.'
        }

        if (isBlank(values.sales_name_in_invoice)) {
          errors.sales_name_in_invoice = 'Nama Sales di Invoice wajib dipilih.'
        }

        if (isBlank(values.deal_by)) {
          errors.deal_by = 'Deal By wajib dipilih.'
        }

        if (isBlank(firstAssistBy)) {
          errors.assist_by_list = ['Assist By 1 wajib dipilih.']
        }

        if (isBlank(values.created_deal_by)) {
          errors.created_deal_by = 'Create Deal By wajib dipilih.'
        }

        if (isBlank(values.sales_method)) {
          errors.sales_method = 'Metode Penjualan wajib dipilih.'
        }
        // DP amount: optional for MIDTRANS (CC), required for others
        if (isMidtransCC) {
          if (dp !== null && dp <= 0) {
            errors.down_payment = 'Jika diisi, nominal DP harus lebih dari 0.'
          }
        } else {
          if (dp === null || dp <= 0) {
            errors.down_payment = 'Mohon Isi Down Payment (DP).'
          }
        }

        const finalPaymentForDpValidation = getDpValidationFinalPayment(values)
        if (
          dp !== null &&
          finalPaymentForDpValidation !== null &&
          dp > finalPaymentForDpValidation
        ) {
          errors.down_payment = 'Down Payment tidak boleh lebih besar dari Final Payment.'
        }

        // DP proof: optional for MIDTRANS (CC), required for others
        if (!isMidtransCC && (!files || files.length === 0)) {
          errors.dp_transfer_proofs = 'Upload bukti transfer DP terlebih dahulu.'
        }

        if (!toIntOrNull(values.po_designer_id)) {
          errors.po_designer_id = 'Pilih Brand / Designer terlebih dahulu.'
        }

        if (!toIntOrNull(values.po_category_id)) {
          errors.po_category_id = 'Pilih Category terlebih dahulu.'
        }

        if (!items || items.length === 0) {
          errors.items = 'PO item belum siap (PO product belum ter-load).'
        } else {
          const itemErrors = []
          items.forEach((item, index) => {
            if (!isItemComplete(item)) {
              itemErrors[index] = {
                selling_price: 'Final Price wajib diisi dan harus lebih dari 0.',
              }
            }
          })
          if (itemErrors.length > 0) errors.items = itemErrors
        }

        if (!toIntOrNull(values.po_condition_id)) {
          errors.po_condition_id = 'Pilih condition terlebih dahulu.'
        }

        if (!(values.po_comes_with || '').trim()) {
          errors.po_comes_with = 'Isi kelengkapan barang terlebih dahulu.'
        }

        if (EDC_PAYMENT_METHODS.includes(methodValue) && !values.card_type) {
          errors.card_type = 'Card Type wajib dipilih untuk EDC BRI / EDC BCA.'
        }

        if (items.length > 0 && items.every(isItemComplete) && isBlank(values.courier)) {
          errors.courier = 'Kurir wajib dipilih.'
        }

        if (!isBlank(values.courier) && isBlank(firstPaymentMethod)) {
          errors.payment_method_list = ['Metode Pembayaran 1 wajib dipilih.']
        }

        return errors
      }}
      onSubmit={ (values, formikBag) => onSubmitForm({
        ...values,
        shipment_cost: toNumberOrNull(values.shipment_cost) ?? 0,
        payment_method: normalizeMidtransVirtualAccountPaymentMethod(values.payment_method),
        payment_method_list: normalizeMidtransVirtualAccountPaymentMethodList(values.payment_method_list),
      }, formikBag) }
    >
      {(formikBag) => {
        const addNewUser = !formikBag.values.use_existing_user

        const items = formikBag.values.items || []
        const allItemsComplete = items.length > 0 && items.every(isItemComplete)
        const hasAtLeastOneItem = items.length > 0
        const showCardType = EDC_PAYMENT_METHODS.includes(primaryPaymentMethodValue)
        const isMidtransCCSelected = isMidtransCreditCardPaymentMethod(formikBag.values.payment_method)

        const shouldShowSalesMethodNote = (salesMethod) => {
          return SALES_METHOD_NOTE_REQUIRED_METHODS.includes(salesMethod)
        }

        const getSalesMethodNoteLabel = (salesMethod) => {
          return SALES_METHOD_NOTE_LABEL_BY_METHOD[salesMethod] || 'Sales Method Note'
        }

        return (
          <>
            <style>
              {`
                .bnk-submit-error-target {
                  border: 1px solid #dc3545 !important;
                  border-radius: 6px;
                  padding: 10px;
                  background: #fff5f5;
                  scroll-margin-top: ${MOBILE_SCROLL_OFFSET}px;
                }

                @media (max-width: 480px) {
                  .bnk-submit-error-target {
                    padding: 8px;
                  }
                }
              `}
            </style>
            <PaymentMethodSyncer />
            <PaymentMethodCardTypeSyncer onPrimaryPaymentMethodChange={ setPrimaryPaymentMethodValue } />
            <SubmitErrorSummarySyncer
              enabled={submitErrorSummary.length > 0}
              setSubmitErrorSummary={setSubmitErrorSummary}
            />

            <Form onSubmit={(e) => e.preventDefault()}>

              {/* ✅ keep assist_by string always updated */}
              <AssistBySyncer />

              {/* ✅ load + auto-add PO placeholder product based on SA Brand/Category */}
              <PoProductMetaLoader
                setPoProduct={setPoProduct}
                setPoProductLoading={setPoProductLoading}
                setPoProductError={setPoProductError}
              />
              <PoProductBootstrapper poProduct={poProduct} />

              {/* =========================
                  QUICK GUIDE (NEW FLOW)
                 ========================= */}
              <Alert color="info" style={{ marginBottom: 18 }}>
                <b>BKO / PO Flow (NEW):</b><br/>
                1) <b>Created</b> (SA buat Deals PO + PO product + bukti transfer DP)<br/>
                2) <b>dp_confirmed</b> (Finance approve DP)<br/>
                3) <b>on_sources</b> (lagi dicari)<br/>
                3.5) <b>sourced</b> (Buying create SKU + supplier, barang dikirim ke BNK)<br/>
                4) <b>arrived</b> (Inbound terima barang)<br/>
                5) <b>full_payment_confirm</b> (SA upload bukti transfer pelunasan)<br/>
                6) <b>shipment_process</b> (Outbound proses kirim seperti deals biasa)<br/>
                7) <b>on_delivery</b> (Outbound)<br/>
                8) <b>complete</b> (SA)
              </Alert>

              {/* =========================
                  1) CUSTOMER
                 ========================= */}
              <BlackSectionHeader
                title="DATA CUSTOMER"
                subtitle={<>⚠️ Semua data di bawah ini adalah data <b>PEMBELI</b>, bukan data Sales.</>}
              />

              <SectionTitle
                title="Customer"
                subtitle={<>Pilih apakah customer <b>sudah terdaftar</b> atau <b>customer baru</b>.</>}
              />

              <ToggleField
                type='checkbox'
                name='use_existing_user'
                label='Customer sudah terdaftar di sistem?'
                onChange={() => {
                  if(formikBag.values.use_existing_user){
                    // switch to NEW customer
                    formikBag.setFieldValue('use_existing_user', false)

                    formikBag.setFieldValue('user', null)
                    formikBag.setFieldValue('user_id', null)
                    formikBag.setFieldValue('email', "")
                    formikBag.setFieldValue('password', "")
                    formikBag.setFieldValue('birthdate', null)

                    formikBag.setFieldValue('first_name', "")
                    formikBag.setFieldValue('last_name', "")
                    formikBag.setFieldValue('name', "")

                    formikBag.setFieldValue('phone_code', null)
                    formikBag.setFieldValue('phone', "")
                    formikBag.setFieldValue('instagram_account', "")
                    formikBag.setFieldValue('lead_source', null)
                    formikBag.setFieldValue('complete_address', "")
                    formikBag.setFieldValue('recipient_name', "")
                    formikBag.setFieldValue('recipient_number', "")
                    formikBag.setFieldValue('instruction', "")
                    formikBag.setFieldValue('latitude', "")
                    formikBag.setFieldValue('longitude', "")

                    // reset selections that depend on customer
                    formikBag.setFieldValue('courier', null)
                    formikBag.setFieldValue('shipment_cost', 0)
                    formikBag.setFieldValue('payment_method_list', [''])
                    formikBag.setFieldValue('payment_method', null)
                    formikBag.setFieldValue('card_type', null)
                  } else {
                    // switch to EXISTING customer
                    formikBag.setFieldValue('use_existing_user', true)
                  }
                }}
              />

              <FormText style={{ marginTop: -8, marginBottom: 16 }}>
                • <b>NYALA</b> = cari customer yang sudah ada<br/>
                • <b>MATI</b> = input data customer baru
              </FormText>

              {formikBag.values.use_existing_user && (
                <>
                  <div data-required-field='user'>
                    <UserSelectSearchField
                      name='user'
                      label='Cari Customer (email / nama / nomor HP)'
                      onSelect={(selectedUser) => {
                      const fullName = selectedUser?.name || ''
                      const { first, last } = splitFullName(fullName)

                      formikBag.setFieldValue('user_id', selectedUser.id)
                      formikBag.setFieldValue('email', selectedUser.email)
                      formikBag.setFieldValue('birthdate', selectedUser.birthdate)

                      formikBag.setFieldValue('first_name', first)
                      formikBag.setFieldValue('last_name', last)
                      formikBag.setFieldValue('name', buildFullName(first, last) || fullName)

                      formikBag.setFieldValue('phone_code', selectedUser.phone_code)
                      formikBag.setFieldValue('phone', selectedUser.phone)
                      formikBag.setFieldValue('instagram_account', selectedUser.instagram_account)
                      formikBag.setFieldValue('lead_source', selectedUser.lead_source)
                      formikBag.setFieldValue('complete_address', selectedUser.complete_address)
                      formikBag.setFieldValue('recipient_name', selectedUser.recipient_name)
                      formikBag.setFieldValue('recipient_number', selectedUser.recipient_number)
                      formikBag.setFieldValue('instruction', selectedUser.instruction)
                      formikBag.setFieldValue('latitude', selectedUser.latitude)
                      formikBag.setFieldValue('longitude', selectedUser.longitude)

                      fetchAndApplyUserMeta(selectedUser.id, formikBag, selectedUser.user_detail_id)
                      }}
                    />
                  </div>

                {/* ===== Detail/Address selector (loaded from SalesUsersApi.show) ===== */}
                {
                  formikBag.values.use_existing_user === true &&
                  formikBag.values.user_id &&
                  (
                    <Row className="mt-3">
                      <Col md="6">
                        <Card className="mb-3">
                          <CardBody>
                            <h5 className="mb-2">Customer Detail (UserDetail)</h5>

                            <Label className="font-weight-bold">Pilih Detail</Label>
                            <Input
                              type="select"
                              value={formikBag.values.user_detail_id || ''}
                              disabled={isUserMetaLoading || !userMeta}
                              onChange={(e) => {
                                const id = e.target.value ? parseInt(e.target.value, 10) : null
                                formikBag.setFieldValue('user_detail_id', id)
                                const detail = (userMeta?.details || []).find(d => d?.id === id)
                                if (detail) applyUserDetailToFormik(detail, formikBag)
                              }}
                            >
                              <option value="" disabled>-- pilih detail --</option>
                              {(userMeta?.details || []).map((d) => (
                                <option key={d.id} value={d.id}>
                                  {getDetailLabel(d)}
                                </option>
                              ))}
                            </Input>

                            {isUserMetaLoading && (
                              <FormText className="text-muted">Loading user details…</FormText>
                            )}

                            {userMetaError && (
                              <Alert color="danger" className="mt-2">
                                {userMetaError}
                              </Alert>
                            )}
                          </CardBody>
                        </Card>
                      </Col>

                      <Col md="6">
                        <Card className="mb-3">
                          <CardBody>
                            <h5 className="mb-2">Shipping Address</h5>

                            <Label className="font-weight-bold">Pilih Address</Label>
                            <Input
                              type="select"
                              value={formikBag.values.address_id || ''}
                              disabled={isUserMetaLoading || !userMeta}
                              onChange={(e) => {
                                const id = e.target.value ? parseInt(e.target.value, 10) : null
                                formikBag.setFieldValue('address_id', id)
                                const addr = (userMeta?.addresses || []).find(a => a?.id === id)
                                if (addr) applyUserAddressToFormik(addr, formikBag)
                              }}
                            >
                              <option value="" disabled>-- pilih address --</option>
                              {(userMeta?.addresses || []).map((a) => (
                                <option key={a.id} value={a.id}>
                                  {getAddressLabel(a)}
                                </option>
                              ))}
                            </Input>

                            <FormText className="text-muted">
                              Memilih address akan mengisi otomatis field alamat + koordinat (jika ada).
                            </FormText>
                          </CardBody>
                        </Card>
                      </Col>
                    </Row>
                  )
                }

                  <ToggleField
                    type="checkbox"
                    name="hide_existing_user_detail"
                    label="Sembunyikan detail customer (biar ringkas)"
                  />
                  <FormText style={{ marginTop: -8, marginBottom: 16 }}>
                    Jika dimatikan, kamu bisa melihat & mengubah data customer.
                  </FormText>
                </>
              )}

              {
                (
                  (formikBag.values.use_existing_user && formikBag.values.user_id)
                  || addNewUser
                )
                &&
                <>
                  {(addNewUser || formikBag.values.hide_existing_user_detail === false) && (
                    <>
                      <InputField
                        type='text'
                        name='email'
                        label='Email Customer'
                        required={ true }
                        formText="⚠️ Pastikan ini email CUSTOMER, bukan email Sales."
                        onBlur={(e) => {
                          if(addNewUser){
                            checkUserEmailIsUnique(e.target.value)
                          }
                        }}
                      />

                      <InputField
                        type='password'
                        name='password'
                        label='Password Customer (opsional)'
                        formText='Jika password tidak diisi, akan diisi random oleh sistem.'
                      />

                      <InputField type='date' label='Tanggal Lahir (opsional)' name='birthdate' />

                      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                        <div style={{ flex: 1, minWidth: 240 }}>
                          <InputField
                            type='text'
                            name='first_name'
                            label='Nama Depan'
                            onChange={(e) => {
                              const v = e.target.value
                              formikBag.setFieldValue('first_name', v)
                              syncFullNameIntoLegacyName(formikBag, v, formikBag.values.last_name)
                            }}
                          />
                        </div>

                        <div style={{ flex: 1, minWidth: 240 }}>
                          <InputField
                            type='text'
                            name='last_name'
                            label='Nama Belakang'
                            onChange={(e) => {
                              const v = e.target.value
                              formikBag.setFieldValue('last_name', v)
                              syncFullNameIntoLegacyName(formikBag, formikBag.values.first_name, v)
                            }}
                          />
                        </div>
                      </div>

                      <PhoneCodeSelectSearchField
                        name='phone_code'
                        label='Kode Negara (Phone Code)'
                        required={ false }
                        onSelect={(selectedOption) => {
                          formikBag.setFieldValue('phone_code', selectedOption.phone_code)
                          if(addNewUser){
                            checkUserPhoneIsUnique(selectedOption.phone_code + (formikBag.values.phone || ""))
                          }
                        }}
                      />
                      <FormText style={{marginTop: "-20px", marginBottom: "20px", fontWeight: "bold"}}>
                        Jika belum ada tanda X, berarti belum dipilih.
                      </FormText>

                      <InputField
                        type='text'
                        name='phone'
                        label='Nomor HP Customer'
                        placeholder="8123456789"
                        formText="Isi nomor HP tanpa spasi/tanda baca. Jangan masukkan phone code (+62)."
                        onBlur={(e) => {
                          const filteredPhone = (e.target.value || "").replace(/\D/g, '')
                          formikBag.setFieldValue('phone', filteredPhone)
                          if(addNewUser){
                            checkUserPhoneIsUnique((formikBag.values.phone_code || "") + filteredPhone)
                          }
                        }}
                      />

                      <InputField
                        type='text'
                        name='instagram_account'
                        label='Instagram Customer (opsional)'
                        placeholder="@username"
                      />

                      <LeadSourceSelectSearchField
                        name='lead_source'
                        label='Customer tahu Belle & Kate dari mana? (opsional)'
                        required={ false }
                      />
                    </>
                  )}

                  <SectionTitle
                    title="Alamat Pengiriman"
                    subtitle={<>Isi alamat customer. Koordinat membantu menampilkan opsi kurir lebih lengkap.</>}
                  />

                  <InputField type='text' name='complete_address' label='Alamat Lengkap' />
                  <InputField type='text' name='recipient_name' label='Nama Penerima' />
                  <InputField
                    type='text'
                    name='recipient_number'
                    label='Nomor Penerima'
                    placeholder="+628123456789"
                    formText="Phone code perlu dimasukkan di sini (contoh: +62...)"
                  />
                  <InputField type='text' name='instruction' label='Instruksi Tambahan (opsional)' placeholder='Misal: titip resepsionis' />

                  <PinpointMovedNote userId={formikBag.values.user_id} />

                  {/* Render GMaps */}

                  {/* {gmapLoaded && !showPinpoint && (
                    <Button color='primary' onClick={() => setShowPinpoint(true)} style={{ marginBottom: 10 }}>
                      { '📍 Pilih Koordinat (opsional)' }
                    </Button>
                  )} */}

                  {/* {gmapLoaded && showPinpoint && (
                    <UserAddressPinpoint
                      lat={ parseFloat(formikBag.values.latitude) }
                      long={ parseFloat(formikBag.values.longitude) }
                      setFieldValue={ formikBag.setFieldValue }
                      closePinpoint={ () => { setShowPinpoint(false) } }
                    />
                  )} */}
                </>
              }

              {formikBag.values.use_existing_user && formikBag.values.user_id && (
                <>
                  <ToggleField type='checkbox' name='edit_existing_user' label='Simpan perubahan data customer' />
                  <FormText style={{ marginTop: -8 }}>
                    Nyalakan hanya jika kamu memang mengubah data customer dan ingin disimpan.
                  </FormText>
                </>
              )}

              <hr />

              {/* =========================
                  2) DEAL BY / ASSIST BY
                 ========================= */}
              <SectionTitle
                title="Deal By / Assist By"
                subtitle={<>• <b>Deal By</b> = Sales yang closing • <b>Assist By</b> = Sales yang membantu (bisa lebih dari 1)</>}
              />

              <div data-required-field='sales_name_in_invoice'>
                <SalesAliasSelectSearchField
                  name='sales_name_in_invoice'
                  label='Nama Sales di Invoice'
                  required={ true }
                />
              </div>

              <div data-required-field='deal_by'>
                  <div data-required-field='deal_by'>
                    <SalesNameSelectSearchField
                      name='deal_by'
                      label='Deal By'
                      required={ true }
                    />
                  </div>
              </div>

              {/* ✅ Assist By multiple */}
              <FieldArray
                name="assist_by_list"
                render={(arrayHelpers) => {
                  const list = formikBag.values.assist_by_list || ['']

                  return (
                    <div className="mt-2">
                      <div className="mb-2">
                        <Label style={{ fontWeight: 700 }}>Assist By (bisa lebih dari 1)</Label>
                        <FormText className="text-muted">
                          Isi minimal 1. Klik “Tambah Assist” untuk menambah assisting.
                        </FormText>
                      </div>

                      {list.map((_, idx) => {
                        const fieldName = `assist_by_list.${idx}`
                        const isFirst = idx === 0

                        return (
                          <div
                            key={fieldName}
                            style={{
                              border: '1px solid #eee',
                              borderRadius: 6,
                              padding: 12,
                              marginBottom: 10,
                              background: '#fafafa',
                            }}
                          >
                            <div data-required-field={fieldName}>
                              <SalesNameSelectSearchField
                                name={fieldName}
                                label={`Assist By ${idx + 1}`}
                                required={isFirst}
                              />
                            </div>

                            {!isFirst && (
                              <Button
                                type="button"
                                color="danger"
                                size="sm"
                                onClick={() => {
                                  arrayHelpers.remove(idx)
                                  const next = (formikBag.values.assist_by_list || []).filter((_, i) => i !== idx)
                                  if (next.length === 0) arrayHelpers.push('')
                                }}
                              >
                                Hapus Assist
                              </Button>
                            )}
                          </div>
                        )
                      })}

                      <Button
                        type="button"
                        color="secondary"
                        onClick={() => arrayHelpers.push('')}
                      >
                        + Tambah Assist
                      </Button>
                    </div>
                  )
                }}
              />

              <hr />

              {/* =========================
                  3) METODE PENJUALAN
                 ========================= */}
              <SectionTitle
                title="Metode Penjualan"
                subtitle={<>Pilih metode penjualan yang paling sesuai dengan konteks deal.</>}
              />

              <InputField
                type='select'
                name='sales_method'
                label='Metode Penjualan'
                required={ true }
                formText='Pilih Regular Purchase apabila customer membeli di luar event apapun'
              >
                <InputSelectOptions options={ SALES_METHOD_CREATE_OPTIONS } keyPrefix={ 'sales_method' } />
              </InputField>

              {shouldShowSalesMethodNote(formikBag.values.sales_method) && (
                <InputField
                  type='text'
                  name='sales_method_note'
                  label={getSalesMethodNoteLabel(formikBag.values.sales_method)}
                  required={ true }
                  placeholder='Contoh: Nama influencer / akun Instagram'
                  formText='Isi nama influencer / akun yang menjadi sumber deal ini.'
                />
              )}

              <hr />

              {/* =========================
                  4) CREATE DEAL BY
                 ========================= */}
              <SectionTitle
                title="Create Deal By"
                subtitle={<>Tentukan sumber pembuatan deal. Default <b>Admin</b> untuk deal dari dashboard.</>}
              />

              <InputField
                type='select'
                name='created_deal_by'
                label='Create Deal By'
                required={ true }
                formText='Default Admin untuk deal yang dibuat oleh dashboard. Pilih Web hanya jika deal perlu ditandai sebagai berasal dari website.'
              >
                <InputSelectOptions options={ CREATED_DEAL_BY_OPTIONS } keyPrefix={ 'created_deal_by' } />
              </InputField>

              <hr />

              {/* =========================
                  5) ITEMS (PO PLACEHOLDER)
                 ========================= */}
              <SectionTitle
                title="Items (PO Placeholder)"
                subtitle={<>Deal PO dibuat dengan <b>1 item placeholder</b>. Detail barang (nama/supplier/SKU) akan dibuat oleh Buying Team saat <b>sourced</b>.</>}
              />

              {(!formikBag.values.po_designer_id || !formikBag.values.po_category_id) && (
                <Alert color="secondary">Pilih Brand / Designer dan Category dulu untuk menentukan SKU PO placeholder.</Alert>
              )}

              {poProductLoading && (
                <Alert color="secondary">Loading PO placeholder product...</Alert>
              )}
              {poProductError && (
                <Alert color="danger">⚠️ {poProductError}</Alert>
              )}

              {poProduct?.id && (
                <Alert color="primary">
                  PO Placeholder product siap dipakai.
                  {poProduct?.sku ? <> SKU: <b>{poProduct.sku}</b></> : null}
                  {poProduct?.internal_category ? <> — Type: <b>{poProduct.internal_category}</b></> : null}
                  {poProduct?.name ? <> — <b>{poProduct.name}</b></> : null}
                </Alert>
              )}

              <FieldArray
                name="items"
                render={() => (
                  <>
                    {(items || []).length === 0 && (
                      <FormText className="text-muted">
                        Item belum siap. Pastikan PO placeholder product berhasil ter-load.
                      </FormText>
                    )}

                    {(items || []).length > 0 && (
                      <div className="mt-3">
                        {items.map((item, index) => (
                          <div key={index} className="border rounded p-3 mb-3">
                            <h5 className="mb-3">
                              {index + 1}. PO Item
                            </h5>

                            <ThousandsInputField
                              name={`items.${index}.selling_price`}
                              label="Final Price (Wajib)"
                              placeholder="Harga deal ke customer"
                              required={ true }
                              formText="Wajib diisi untuk membuat deal PO."
                            />

                            <InputField
                              type="text"
                              name={`items.${index}.note`}
                              label="Detail Barang yang Di-PO (free text)"
                              placeholder="Contoh: Hermes Kelly 28 Retourne, warna, hardware, kondisi, dll"
                              required={ false }
                              formText="Isi detail agar Buying Team tahu target barang yang harus dicari."
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="mt-4 border rounded p-3">
                      <h5>Detail Barang (Diisi Sales Assistant)</h5>

                      <Row>
                        <Col md={6}>
                          <InputField
                            type="select"
                            name="po_designer_id"
                            label="Brand / Designer (SA) — wajib"
                            required
                            disabled={poMetaLoading}
                          >
                            <option value="">-- pilih brand --</option>
                            {(poMeta.designers || []).map((d) => (
                              <option key={d.id} value={d.id}>
                                {d.code ? `${d.code} - ${d.name}` : d.name}
                              </option>
                            ))}
                          </InputField>
                        </Col>

                        <Col md={6}>
                          <InputField
                            type="select"
                            name="po_category_id"
                            label="Category (SA) — wajib"
                            required
                            disabled={poMetaLoading}
                          >
                            <option value="">-- pilih category --</option>
                            {(poMeta.categories || []).map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.code ? `${c.code} - ${c.name}` : c.name}
                              </option>
                            ))}
                          </InputField>
                        </Col>
                      </Row>

                      <InputField
                        type="select"
                        name="po_condition_id"
                        label="Condition (SA) — wajib"
                        required
                        disabled={poMetaLoading}
                      >
                        <option value="">-- pilih condition --</option>
                        {(poMeta.conditions || []).map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </InputField>

                      <InputField
                        type="textarea"
                        name="po_comes_with"
                        label="Kelengkapan (Comes With) — wajib"
                        placeholder="Contoh: box, dustbag, strap, receipt..."
                      />
                    </div>
                  </>
                )}
              />

              <hr />

              {/* =========================
                  5) PICKUP DATE + METODE PENGIRIMAN
                 ========================= */}
              <SectionTitle
                title="Pickup Date & Metode Pengiriman"
                subtitle={<>Pickup Date <b>opsional</b>. Kurir wajib dipilih setelah item & Final Price lengkap.</>}
              />

              <DateInputField
                label='Pickup Date (Opsional)'
                name='pickup_at'
                dateFormat="dd/MM/yyyy"
                required={ false }
                formText='📦 Tanggal estimasi barang diambil/dikirim. Boleh dikosongkan jika belum pasti. Format: dd/mm/yy'
              />

              {hasAtLeastOneItem && allItemsComplete ? (
                <>
                  <div data-required-field='courier'>
                    <CourierSelectSearchField
                      name='courier'
                      label='Kurir'
                      required={ true }
                      latitude={formikBag.values.latitude}
                      longitude={formikBag.values.longitude}
                      productIdWithSellingPriceList={(items || []).map(o => ({
                        product_id: o.product_id,
                        selling_price: parseFloat(o.selling_price),
                      }))}
                      formText="Tip: isi koordinat alamat agar opsi kurir lebih lengkap."
                    />
                  </div>

                  {formikBag.values.courier && (
                    <ThousandsInputField
                      name='shipment_cost'
                      label='Ongkir Manual'
                      required={ false }
                      placeholder='Masukkan nominal ongkir'
                      formText='Opsional. Nominal ongkir diinput manual sesuai kondisi lapangan. Isi 0 jika tidak ada ongkir.'
                    />
                  )}
                </>
              ) : (
                <Alert color="secondary">
                  Kurir akan muncul setelah:<br/>
                  ✔️ Minimal 1 item siap<br/>
                  ✔️ Final Price terisi
                </Alert>
              )}

              <hr />

              {/* =========================
                  6) ORDER + DP + DP PROOF
                 ========================= */}
              <SectionTitle
                title="Order (DP)"
                subtitle={<>Untuk PO flow, isi <b>DP + Bukti Transfer DP</b> untuk kebutuhan Finance. Khusus <b>MIDTRANS (CC)</b>, keduanya opsional.</>}
              />

              <InputField
                type='text'
                name='voucher_code'
                label='Kode Voucher (opsional)'
                placeholder='NEW500'
                formText='Isi jika pembelian PO/BKO menggunakan voucher'
              />

              <ThousandsInputField
                name='down_payment'
                label={`Down Payment (DP) ${isMidtransCCSelected ? '(Opsional untuk MIDTRANS CC)' : '(Wajib)'}`}
                placeholder='Contoh: 5000000'
                required={!isMidtransCCSelected}
                formText={
                  isMidtransCCSelected
                    ? 'Untuk MIDTRANS (CC), DP boleh dikosongkan. Jika diisi, nilainya harus lebih dari 0.'
                    : 'DP ini akan dipakai Finance untuk proses dp_confirmed (threshold).'
                }
              />

              <div 
                className='mb-3'
                style={{
                  border: formikBag.errors?.dp_transfer_proofs && formikBag.touched?.dp_transfer_proofs ? '1px solid #dc3545' : '1px solid transparent',
                  borderRadius: 6,
                  padding: formikBag.errors?.dp_transfer_proofs && formikBag.touched?.dp_transfer_proofs ? 10 : 0,
                  background: formikBag.errors?.dp_transfer_proofs && formikBag.touched?.dp_transfer_proofs ? '#fff5f5' : 'transparent',
                }}
              >
                <Label className='form-control-label' style={{ fontWeight: 700 }}>
                  Bukti Transfer DP {isMidtransCCSelected ? '(Opsional untuk MIDTRANS CC)' : '(Wajib)'}
                </Label>
                <Input
                  name='dp_transfer_proofs'
                  type='file'
                  multiple
                  accept='image/*,application/pdf'
                  onChange={(e) => {
                    const files = Array.from(e?.currentTarget?.files || [])

                    const MAX_FILES = 10
                    const MAX_SIZE_MB = 5
                    const ALLOWED_TYPES = [
                      'image/jpeg',
                      'image/png',
                      'application/pdf'
                    ]

                    const validFiles = []

                    for (let file of files) {
                      if (!ALLOWED_TYPES.includes(file.type)) {
                        alert(`File ${file.name} tidak didukung`)
                        continue
                      }

                      if (file.size > MAX_SIZE_MB * 1024 * 1024) {
                        alert(`File ${file.name} melebihi ${MAX_SIZE_MB}MB`)
                        continue
                      }

                      validFiles.push(file)
                    }

                    if (validFiles.length > MAX_FILES) {
                      alert(`Maksimal upload ${MAX_FILES} file`)
                      validFiles.splice(MAX_FILES)
                    }

                    formikBag.setFieldValue('dp_transfer_proofs', validFiles)
                    formikBag.setFieldTouched('dp_transfer_proofs', true, false)
                  }}
                />
                <FormText className='text-muted'>
                  {isMidtransCCSelected
                    ? 'Untuk MIDTRANS (CC), upload bukti transfer DP tidak wajib. DP boleh dikosongkan. Jika diisi, nilainya harus lebih dari 0. Upload bisa lebih dari 1 file (Max 10 file) dengan format (JPG/JPEG/PNG/PDF) Max 2MB.'
                    : 'Upload screenshot transfer / bukti pembayaran DP. Upload ini wajib. Upload bisa lebih dari 1 file (Max 10 file) dengan format (JPG/JPEG/PNG/PDF) Max 2MB.'}
                </FormText>

                {formikBag.errors?.dp_transfer_proofs && formikBag.touched?.dp_transfer_proofs && (
                  <div style={{ color: '#dc3545', marginTop: 6 }}>
                    {formikBag.errors.dp_transfer_proofs}
                  </div>
                )}

                {(formikBag.values.dp_transfer_proofs || []).length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <Label style={{ fontWeight: 600 }}>Preview Bukti Transfer DP</Label>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      {(formikBag.values.dp_transfer_proofs || []).map((file, idx) => {
                        const isPdf = file?.type?.includes('pdf')
                        const previewUrl = URL.createObjectURL(file)

                        return (
                          <div
                            key={`${file.name}-${idx}`}
                            style={{
                              border: '1px solid #ddd',
                              borderRadius: 6,
                              padding: 10,
                              position: 'relative',
                            }}
                          >
                            <Button
                              size='sm'
                              color='danger'
                              type='button'
                              style={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                padding: '2px 8px',
                              }}
                              onClick={() => {
                                const updated = [...(formikBag.values.dp_transfer_proofs || [])]
                                updated.splice(idx, 1)
                                formikBag.setFieldValue('dp_transfer_proofs', updated)
                              }}
                            >
                              ✕
                            </Button>

                            {isPdf ? (
                              <>
                                <div style={{ fontWeight: 600 }}>📄 PDF File</div>
                                <div style={{ fontSize: 13 }}>{file.name}</div>
                              </>
                            ) : (
                              <>
                                <img
                                  src={previewUrl}
                                  alt={`dp-proof-${idx}`}
                                  style={{
                                    width: '100%',
                                    maxHeight: 320,
                                    objectFit: 'contain',
                                    borderRadius: 4,
                                  }}
                                />
                                <div style={{ fontSize: 12, marginTop: 6 }}>
                                  {file.name}
                                </div>
                              </>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>

              <InputField
                type='text'
                name='note'
                label='Catatan (opsional)'
                placeholder='Catatan internal untuk PO/BKO'
                formText='Catatan internal untuk order/deal'
              />

              <hr />

              {/* =========================
                  7) PAYMENT METHOD (LAST)
                 ========================= */}
              <SectionTitle
                title="Metode Pembayaran"
                subtitle={<>Metode pembayaran baru bisa dipilih setelah kurir dipilih.</>}
              />

              {formikBag.values.courier ? (
                <>
                  <FieldArray
                    name='payment_method_list'
                    render={(arrayHelpers) => {
                      const list = formikBag.values.payment_method_list || ['']

                      return (
                        <>
                          {list.map((_, idx) => (
                            <div key={`pm_${idx}`} className='border rounded p-2 mb-2'>
                              <div data-required-field={`payment_method_list.${idx}`}>
                                <PaymentMethodSelectSearchField
                                  name={`payment_method_list.${idx}`}
                                  label={`Metode Pembayaran ${idx + 1}`}
                                  required={idx === 0}
                                  variant='deal_v2'
                                />
                              </div>

                              {idx > 0 && (
                                <Button
                                  type='button'
                                  color='danger'
                                  size='sm'
                                  onClick={() => arrayHelpers.remove(idx)}
                                >
                                  Hapus Metode
                                </Button>
                              )}
                            </div>
                          ))}

                          <Button type='button' color='secondary' onClick={() => arrayHelpers.push('')}>
                            + Tambah Metode Pembayaran
                          </Button>

                          <FormText className='text-muted mt-2'>
                            Catatan: untuk sementara backend lama tetap memakai <b>metode pembayaran pertama</b>.
                          </FormText>

                           <FormText className='text-muted mt-1'>
                            Untuk Midtrans CC, pilih langsung opsi <b>Non Amex</b> atau <b>Amex</b> di list metode pembayaran.
                          </FormText>
                        </>
                      )
                    }}
                  />

                  {showCardType && (
                    <div className='mt-3'>
                      <InputField
                        type='select'
                        name='card_type'
                        label='Card Type'
                        required={ true }
                      >
                        <option value=''>-- pilih card type --</option>
                        {CARD_TYPE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </InputField>

                      <FormText className='text-muted'>
                        Card Type hanya berlaku untuk <b>Metode Pembayaran 1</b> karena backend lama
                        masih membaca payment pertama.
                      </FormText>
                    </div>
                  )}
                </>
              ) : (
                <Alert color="secondary">
                  Pilih <b>Kurir</b> dulu untuk lanjut pilih metode pembayaran.
                </Alert>
              )}

              <br />

              {submitErrorSummary.length > 0 && (
                <Alert color='danger'>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>
                    Mohon lengkapi data wajib berikut:
                  </div>
                  <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
                    {submitErrorSummary.map((msg, idx) => (
                      <li key={`${msg}-${idx}`}>{msg}</li>
                    ))}
                  </ul>
                </Alert>
              )}

              <Button
                color='primary'
                disabled={formikBag.isSubmitting || poProductLoading || !!poProductError || !poProduct?.id}
                onClick={async () => {
                  const errors = await formikBag.validateForm()
                  const touchedFromErrors = buildTouchedFromErrors(errors)
                  formikBag.setTouched(
                    mergeDeep(formikBag.touched, touchedFromErrors),
                    false
                  )

                  if (Object.keys(errors).length > 0) {
                    setSubmitErrorSummary(buildSubmitErrorSummary(errors))

                    const firstErrorField = getFirstErrorField(errors)
                    const firstErrorLabel = getFieldLabel(firstErrorField)

                    onValidationError(firstErrorLabel)
                    scrollToFirstInvalidField(firstErrorField)

                    return
                  }

                  setSubmitErrorSummary([])
                  clearSubmitErrorTarget()
                  formikBag.submitForm()

                }}
              >
                { 'Create Deal (PO / BKO)' }
              </Button>
            </Form>
          </>
        )
      }}
    </Formik>
  )
}

SalesDealsBkoCreateForm.propTypes = propTypes
export default SalesDealsBkoCreateForm
